import type { FC } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { Dialog, formatDateTimeFromISO, LoadingSpinner } from "widgets";
import { type ControlledDialog, useControlledDialog, useVisuallyLoading } from "widgets/hooks";

import { fileNameValidation, simpleFieldValidation, urlValidation } from "@/app-utils/form.ts";
import { query as Q } from "@/app-utils/graphql/query.ts";
import { useError } from "@/app-utils/graphql/useError.ts";
import type { InputModifyProjectFileInput, Organism } from "@/graphql.ts";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import {
  useGetFileEditingQuery,
  ValidateFileNamesDocument,
  type ValidateFileNamesQuery,
  type ValidateFileNamesQueryVariables,
} from "@/modules/files/graphql/files-queries.gql";
import { getFileInfo, isAnalysisSetFile, isGenomeViewerFile } from "@/modules/files/utils/file";
import { updateProjectFile } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { FILE_BROWSE_TYPES, MAX_LENGTH } from "@/settings/settings.ts";
import { Addon } from "@/ui-modules/core/redux-form/Addon.tsx";
import InputField, { InputReduxFormField } from "@/ui-modules/core/redux-form/InputField.tsx";
import { SequencedGenomeSelector } from "@/ui-modules/files/SequencedGenomeSelector.tsx";

interface FileEditDialogProps extends ControlledDialog {
  fileId: number;
  onSaved?: () => void;
}

enum FileInfoEditDialogFormNames {
  FORM_NAME = "fileEditingForm",
}

enum FileInfoEditDialogFieldNames {
  NAME_FIELD_NAME = "NAME_FIELD_NAME",
  DESCRIPTION_FIELD_NAME = "DESCRIPTION_FIELD_NAME",
  EDITED_FILE = "EDITED_FILE",
  GENOME_FIELD_NAME = "GENOME_FIELD_NAME",
  URL_FIELD_NAME = "URL_FIELD_NAME",
}

const query = Q<ValidateFileNamesQuery, ValidateFileNamesQueryVariables>(ValidateFileNamesDocument);

const uniqueFileNameValidation = async (values: Partial<FormData>) => {
  if (
    values[FileInfoEditDialogFieldNames.NAME_FIELD_NAME] !== values[FileInfoEditDialogFieldNames.EDITED_FILE]?.fileName
  ) {
    const folderId = values[FileInfoEditDialogFieldNames.EDITED_FILE]?.parentFolderIds?.slice(-1)?.[0];

    if (folderId != undefined) {
      const result = await query({
        folderId,
        fileNames: [values[FileInfoEditDialogFieldNames.NAME_FIELD_NAME]],
      });

      if (result.data?.fileNameExistsInCollector?.length) {
        throw {
          [FileInfoEditDialogFieldNames.NAME_FIELD_NAME]: "This name already exists.",
        };
      }
    }
  }
  return;
};

const FileEditDialogImpl: FC<FileEditDialogProps & InjectedFormProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { dialogProps, underlayProps, dialogRef } = useControlledDialog(props.onCancel);

  // Fetch editable file information
  const { data, loading, error } = useGetFileEditingQuery({
    variables: { fileId: props.fileId },
  });
  void useError(error);

  const file = data?.projectFileById;

  const { baseName, extension } = getFileInfo(file?.fileName ?? "", file?.fileBrowseType ?? "");

  const handleSubmitData = async (values: Partial<FormData>) => {
    const newFileName: string = values[FileInfoEditDialogFieldNames.NAME_FIELD_NAME];
    const newURL: string = values[FileInfoEditDialogFieldNames.URL_FIELD_NAME];
    const description: string = values[FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME];
    const selectedOrganisms: Organism[] = values[FileInfoEditDialogFieldNames.GENOME_FIELD_NAME];
    const organismIds: number[] = selectedOrganisms ? selectedOrganisms.map((o: Organism) => o.id) : [];

    const modifyProjectFileInput: InputModifyProjectFileInput = {
      id: file?.id,
      name: newFileName?.concat(extension),
      url: newURL,
      description,
      organismIds,
      isProtected: file?.isProtected,
    };

    return await dispatch(updateProjectFile(modifyProjectFileInput))
      .then(props.onSaved)
      .catch((error) => {
        throw new SubmissionError({ _error: error?.message || error });
      });
  };

  const fakeLoading = useVisuallyLoading(loading, 300);

  useEffect(() => {
    if (file) {
      props.initialize({
        [FileInfoEditDialogFieldNames.NAME_FIELD_NAME]: baseName,
        [FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME]: file.description,
        [FileInfoEditDialogFieldNames.EDITED_FILE]: file,
        [FileInfoEditDialogFieldNames.URL_FIELD_NAME]: file.url,
        [FileInfoEditDialogFieldNames.GENOME_FIELD_NAME]: file.organisms ?? [],
      });
    }
  }, [file]);

  return (
    <Dialog
      ref={dialogRef}
      title={file?.fileName}
      subtitle={
        file?.lastModified ? `Modified on ${formatDateTimeFromISO(file?.lastModified, { hideTime: true })}` : undefined
      }
      icon={<HeaderIcon file={file} />}
      closeFn={() => props.onCancel?.()}
      domProps={dialogProps}
      underlayProps={underlayProps}
      width={580}
      errorCallout={{ message: props.error }}
      mainButton={{
        label: "Save",
        onPress: props.handleSubmit(handleSubmitData),
      }}
    >
      {fakeLoading ? (
        <LoadingSpinner />
      ) : (
        file && (
          <>
            <InputReduxFormField
              componentClass="input"
              name={FileInfoEditDialogFieldNames.NAME_FIELD_NAME}
              label={"Name"}
              placeholder={`Enter a name`}
              maxLength={MAX_LENGTH.RESOURCE.NAME}
              component={InputField}
              key={FileInfoEditDialogFieldNames.NAME_FIELD_NAME}
              validate={[simpleFieldValidation, fileNameValidation]}
              suffix={<Addon label={extension} />}
            />
            {file?.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK && (
              <InputReduxFormField
                componentClass="input"
                name={FileInfoEditDialogFieldNames.URL_FIELD_NAME}
                label={"Link"}
                maxLength={MAX_LENGTH.RESOURCE.URL}
                type="url"
                placeholder={`Enter link or URL`}
                component={InputField}
                validate={urlValidation}
              />
            )}
            {(file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE ||
              file?.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY ||
              file?.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK) && (
              <InputReduxFormField
                componentClass="input"
                name={FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME}
                label={"Description"}
                placeholder={`Enter a description`}
                maxLength={MAX_LENGTH.RESOURCE.DESCRIPTION}
                component={InputField}
                key={FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME}
              />
            )}
            {file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE &&
              (isAnalysisSetFile(file) || isGenomeViewerFile(file) || file?.organisms?.length > 0) && (
                <SequencedGenomeSelector
                  fieldName={FileInfoEditDialogFieldNames.GENOME_FIELD_NAME}
                  name={"Genome"}
                  submitting={false}
                  clearable={true}
                  multiple={true}
                />
              )}
          </>
        )
      )}
    </Dialog>
  );
};

export const FileEditDialogForm = reduxForm({
  form: FileInfoEditDialogFormNames.FORM_NAME,
  asyncBlurFields: [FileInfoEditDialogFieldNames.NAME_FIELD_NAME],
  asyncValidate: uniqueFileNameValidation,
})(FileEditDialogImpl) as unknown;
